import React, { useRef, useState, useEffect } from "react"
import { Container, Sprite, withFilters, useTick } from "@inlet/react-pixi"
import Blur from "components/blur"
import ActiveHighlighter from "components/active-highlighter"
import ReelItem from "components/reel-item"
import { clamp, easeInOut, easeIn, easeOut, lerp, sas } from "utils/math"

import { filters } from "pixi.js"

const FilteredSprite = withFilters(Sprite, { blur: filters.BlurFilter })

// Backout function from tweenjs.
// https://github.com/CreateJS/TweenJS/blob/master/src/tweenjs/Ease.js
function backout(amount) {
  return t =>
    t > 0.001
      ? --t * t * ((amount + 1) * t + amount) + 1
      : 1 - sqr(sqr(sqr(2.4 * t - 1.2)))
}

const backInOut = amount => {
  amount *= 1.525
  return function (t) {
    if ((t *= 2) < 1) return 0.5 * (t * t * ((amount + 1) * t - amount))
    return 0.5 * ((t -= 2) * t * ((amount + 1) * t + amount) + 2)
  }
}

const sqr = x => x * x

// phase breakpoints in ms
const PHASE_1 = 200
const PHASE_2 = 180
const PHASE_3 = 40
const PHASE_4 = 300
const PHASE_5 = 150

const phase = (time, state) =>
  state === "start"
    ? time < PHASE_1
      ? 1
      : time < PHASE_1 + PHASE_2
      ? 2
      : 3
    : time < PHASE_4
    ? 4
    : time < PHASE_4 + PHASE_5
    ? 5
    : 6

const Reel = ({
  columnIndex,
  items,
  rollingTime,
  roll,
  scatter,
  gold,
  activeIndex,
  state,
  counter,
  darken,
}) => {
  const time = useRef(0)
  const reset = useRef(false)
  const bref = useRef()
  const currentIndex = useRef(0)
  const animations = items.map(i => useRef("none"))
  const refs = items.map(i => useRef())
  const { length } = items

  useEffect(() => {
    reset.current = true
    if (state === "start") {
      // posiible flicker
      refs.forEach((ref, i) => (ref.current.y = -120))
      animations.forEach((ref, i) => (ref.current = "none"))
      for (let i = -1; i <= 2; i++) {
        refs[sas(roll + i, length)].current.y = 60 * (i + 1)
        //console.log(sas(roll + i, length))
      }
      currentIndex.current = sas(roll - 3, length)
    } else if (state === "stop") {
    }
  }, [state])

  useTick((delta, x) => {
    if (reset.current) {
      reset.current = false
      time.current = state === "start" ? 0 : time.current % PHASE_3
    }
    bref.current.alpha = clamp(
      bref.current.alpha + (x.elapsedMS / 250) * (darken ? -1 : 1),
      0.2,
      1
    )
    //console.log(bref.current.alpha)
    if (!bref.current.filters) {
      bref.current.filters = [new filters.BlurFilter(0)]
    }
    let _t = time.current
    time.current += x.elapsedMS
    let t = time.current
    let v = 0,
      squash = 1

    if (state === "start") {
      // phase 1
      if (t < PHASE_1) {
        v = 0
        let motion = lerp(0, -20, easeInOut(t / PHASE_1))
        for (let i = -1; i <= 2; i++) {
          refs[sas(roll + i, length)].current.y = 60 * (i + 1) + motion
        }
      }
      // phase 2
      if (t >= PHASE_1 && t < PHASE_1 + PHASE_2) {
        v = lerp(0, 12, (t - PHASE_1) / PHASE_2)
        let motion = lerp(-20, 180, easeIn((t - PHASE_1) / PHASE_2))
        for (let i = -4; i <= 2; i++) {
          refs[sas(roll + i, length)].current.y = 60 * (i + 1) + motion
        }
      }
      // phase 3
      if (t >= PHASE_1 + PHASE_2) {
        if (
          _t >= PHASE_2 + PHASE_1 &&
          ~~((t - PHASE_2 - PHASE_1) / PHASE_3) !==
            ~~((_t - PHASE_2 - PHASE_1) / PHASE_3)
        ) {
          currentIndex.current = sas(currentIndex.current - 1, length)
        }
        v = 12
        let motion = lerp(
          0,
          60,
          clamp(((t - (PHASE_1 + PHASE_2)) % PHASE_3) / PHASE_3)
        )
        for (let i = -2; i <= 2; i++) {
          refs[sas(currentIndex.current + i, length)].current.y =
            60 * (i + 1) + motion
        }
        //refs[sas(currentIndex.current - 2, length)].current.y = 0
      }
    } else if (state === "stop") {
      // phase 4
      if (t < PHASE_4 / 2) {
        v = 12
        let motion = lerp(0, 180, clamp((time.current / PHASE_4) * 2))
        for (let i = -2; i <= 2; i++) {
          refs[sas(currentIndex.current + i, length)].current.y =
            60 * (i + 2) + motion
        }

        for (let i = -2; i <= 3; i++) {
          refs[sas(roll + i, length)].current.y = 60 * (i - 4) + motion
        }
      }
      // phase 4.5
      if (t > PHASE_4 / 2 && t < PHASE_4) {
        // reset the symbols that haven't finished moving during the previous phase
        for (let i = -2; i <= 2; i++) {
          refs[sas(currentIndex.current + i, length)].current.y =
            60 * (i + 2) + 180
        }
        v = lerp(0, 12, 1 - ((t - PHASE_4 / 2) / PHASE_4) * 2)
        let motion = lerp(
          0,
          190,
          easeOut(clamp(((t - PHASE_4 / 2) / PHASE_4) * 2), 3)
        )
        for (let i = -2; i <= 3; i++) {
          refs[sas(roll + i, length)].current.y = 60 * (i - 2) + motion
          squash = lerp(0, 1, 1 - ((t - PHASE_4 / 2) / PHASE_4) * 2)
        }
      }
      // phase 5
      if (t > PHASE_4 && t < PHASE_4 + PHASE_5) {
        let motion = lerp(190, 180, easeOut(clamp((t - PHASE_4) / PHASE_5), 3))
        for (let i = -2; i <= 3; i++) {
          refs[sas(roll + i, length)].current.y = 60 * (i - 2) + motion
          //animations[sas(roll + i, length)].current = "squash"
          squash = lerp(0, 1, clamp((t - PHASE_4) / PHASE_5))
        }
      }
      // phase 6
      if (t > PHASE_4 + PHASE_5) {
        for (let i = -2; i <= 3; i++) {
          refs[sas(roll + i, length)].current.y = 60 * (i - 2) + 180
          squash = 1
        }
      }
    }
    bref.current.filters[0].blurY = v * 6
    bref.current.filters[0].blurX = v * 0.25
    //bref.current.scale.y = lerp(0.92, 1, squash)
  })
  return (
    <Container x={columnIndex * 60} ref={bref} y={60}>
      {items.map((item, id) => (
        <ReelItem
          width={60}
          height={60}
          x={0}
          ref={refs[id]}
          y={60 * id}
          animation={
            activeIndex === id
              ? "active"
              : phase(time.current, state) === 3
              ? "blur"
              : "none"
          }
          // eslint-disable-next-line react/no-array-index-key
          key={id}
          i={id}
          item={item}
          counter={counter}
        />
      ))}
    </Container>
  )
}

export default Reel
